import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { WS_HOME } from "../nav";
import { useToast } from "../ui";
import { authenticate, deviceSecurity, loadLock, shouldLock } from "./lock";
import { isNativeRuntime } from "./platform";
import { enablePush, updatePushLocale } from "./push";

const HOMES = new Set(["/", "/login", ...Object.values(WS_HOME)]);

/**
 * Native-only behaviour mounted once inside the router: splash hand-off, Android back button, app lock on resume
 * (biometric / device PIN, or the staff PIN screen on a shared kiosk tablet), push registration and taps.
 */
export function NativeBridge() {
  if (!isNativeRuntime()) return null;
  return <Bridge />;
}

function Bridge() {
  const { me, refresh, logout } = useAuth();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const loc = useLocation();
  const [locked, setLocked] = useState(false);
  const bgAt = useRef<number | null>(null);
  const meRef = useRef(me); meRef.current = me;
  const pathRef = useRef(loc.pathname); pathRef.current = loc.pathname;
  const lastBack = useRef(0);

  useEffect(() => { void import("@capacitor/splash-screen").then(({ SplashScreen }) => SplashScreen.hide({ fadeOutDuration: 200 })).catch(() => undefined); }, []);

  // Android back: close the top dialog/drawer/menu -> go back -> at a home screen, a second press leaves the app
  useEffect(() => {
    let remove: (() => void) | undefined;
    void import("@capacitor/app").then(async ({ App }) => {
      const h = await App.addListener("backButton", ({ canGoBack }) => {
        if (document.querySelector(".kiosk-lock, .app-lock")) { void App.minimizeApp(); return; }
        if (document.querySelector(".overlay, .menu, .scrim")) { document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true })); (document.querySelector(".scrim") as HTMLElement | null)?.click(); return; }
        if (!HOMES.has(pathRef.current) && canGoBack) { navigate(-1); return; }
        if (Date.now() - lastBack.current < 2000) { void App.minimizeApp(); return; }
        lastBack.current = Date.now(); toast(t("mob.back.exitHint"));
      });
      remove = () => void h.remove();
    });
    return () => remove?.();
  }, [navigate, t, toast]);

  // app lock on resume
  useEffect(() => {
    let remove: (() => void) | undefined;
    void import("@capacitor/app").then(async ({ App }) => {
      const h = await App.addListener("appStateChange", async ({ isActive }) => {
        if (!isActive) { bgAt.current = Date.now(); return; }
        const cur = meRef.current;
        const since = bgAt.current; bgAt.current = null;
        if (!cur) return;
        const settings = await loadLock();
        // a shared kiosk tablet goes back to the staff PIN screen (server-enforced, 423 until unlocked)
        if (cur.kiosk && !cur.kiosk.locked && since !== null && Date.now() - since >= settings.afterSeconds * 1000) {
          await api("/api/kiosk/lock", { method: "POST", body: {} }).catch(() => undefined);
          await refresh().catch(() => undefined);
          return;
        }
        const sec = await deviceSecurity();
        if (shouldLock({ settings, backgroundedAt: since, now: Date.now(), signedIn: true, deviceSecure: sec.secure })) setLocked(true);
        else await refresh().catch(() => undefined); // pick up changes made while away
      });
      remove = () => void h.remove();
    });
    return () => remove?.();
  }, [refresh]);

  // push: register for the signed-in member; taps open the related screen
  useEffect(() => {
    if (!me?.membership) return;
    void enablePush({ ask: true, locale: locale as "en" | "zh" | "ms", onTap: (route) => navigate(route), onForeground: (title, body) => toast(t("mob.push.received", { title, body })) });
  }, [me?.membership?.id]);
  useEffect(() => { if (me?.membership) void updatePushLocale(locale as "en" | "zh" | "ms"); }, [locale]);

  if (!locked) return null;
  return <AppLockOverlay onUnlocked={() => { setLocked(false); void refresh().catch(() => undefined); }} onSignOut={() => { setLocked(false); void logout().then(() => navigate("/login")); }} />;
}

function AppLockOverlay({ onUnlocked, onSignOut }: { onUnlocked: () => void; onSignOut: () => void }) {
  const { t } = useI18n();
  const busy = useRef(false);
  const tryUnlock = async () => {
    if (busy.current) return;
    busy.current = true;
    try { if (await authenticate(t("mob.lock.reason"), t("common.back"))) onUnlocked(); } finally { busy.current = false; }
  };
  useEffect(() => { void tryUnlock(); }, []);
  return (
    <div className="overlay center app-lock" role="dialog" aria-modal="true" aria-label={t("mob.lock.title")}>
      <div className="modal stack center-text" style={{ maxWidth: 380 }}>
        <div className="app-lock-mark" aria-hidden>🔒</div>
        <h2>{t("mob.lock.title")}</h2>
        <p className="muted">{t("mob.lock.body")}</p>
        <button className="btn btn-primary btn-xl btn-block" onClick={() => void tryUnlock()}>{t("mob.lock.unlock")}</button>
        <button className="btn btn-quiet" onClick={onSignOut}>{t("mob.lock.signOut")}</button>
      </div>
    </div>
  );
}
